import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import TeeCard from '../components/TeeCard';

export default function ShareTeePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tt, setTt] = useState(null);
  const [joinableGroup, setJoinableGroup] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [joiningGroup, setJoiningGroup] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.getTeeTimeById(id);
      if (rows && rows.length > 0) {
        setTt(rows[0]);
        setJoinableGroup(null);
        setNotFound(false);
      } else {
        // Not visible yet — check whether joining a public group would fix that.
        const info = await api.getTeeTimeAccessInfo(id);
        if (info.joinableGroup) {
          setJoinableGroup(info.joinableGroup);
          setTt(null);
          setNotFound(false);
        } else {
          setNotFound(true);
          setTt(null);
          setJoinableGroup(null);
        }
      }
    } catch {
      setNotFound(true);
      setTt(null);
      setJoinableGroup(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const handleJoinGroup = async () => {
    setJoiningGroup(true);
    try {
      await api.joinTeeTimeViaGroup(id);
      await load();
    } catch (err) {
      alert(err.message);
    } finally {
      setJoiningGroup(false);
    }
  };

  if (!user) {
    return (
      <div style={{ minHeight:'100vh', background:'#f4f1eb', display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
        <div style={{ width:'100%', maxWidth:400 }}>
          <div style={{ textAlign:'center', marginBottom:'1.5rem' }}>
            <h1 style={{ fontSize:24, color:'#0d2b1a', fontFamily:"'Playfair Display', serif" }}>
              Green<span style={{ color:'#2e7d4f' }}>time</span>
            </h1>
          </div>
          <div className="card" style={{ textAlign:'center' }}>
            <div style={{ fontSize:32, marginBottom:8 }}>⛳</div>
            <h3 style={{ marginBottom:8 }}>Someone shared a tee time with you</h3>
            <p style={{ fontSize:13, color:'#7a7a6a', marginBottom:'1.25rem' }}>Sign in to see the details and join.</p>
            <button className="btn btn-accent" style={{ width:'100%', justifyContent:'center' }}
              onClick={() => navigate(`/auth?redirect=/tee/${id}`)}>
              Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="loading-screen">Loading…</div>;

  return (
    <div style={{ minHeight:'100vh', background:'#f4f1eb', padding:'1.25rem' }}>
      <div style={{ maxWidth:420, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:'1.25rem' }}>
          <h1 style={{ fontSize:22, color:'#0d2b1a', fontFamily:"'Playfair Display', serif" }}>
            Green<span style={{ color:'#2e7d4f' }}>time</span>
          </h1>
        </div>

        {tt && (
          <TeeCard
            tt={tt}
            currentUser={user}
            onRefresh={load}
            onViewThread={() => navigate('/browse')}
          />
        )}

        {!tt && joinableGroup && (
          <div className="card" style={{ textAlign:'center' }}>
            <div style={{ width:48, height:48, borderRadius:12, background:joinableGroup.color,
                          display:'flex', alignItems:'center', justifyContent:'center',
                          fontSize:22, margin:'0 auto 0.75rem' }}>{joinableGroup.emoji}</div>
            <h3 style={{ marginBottom:4 }}>This tee time is part of {joinableGroup.name}</h3>
            <p style={{ fontSize:13, color:'#7a7a6a', marginBottom:'1.25rem' }}>
              It's a public group — join to see the details and sign up.
            </p>
            <button className="btn btn-accent" style={{ width:'100%', justifyContent:'center' }}
              onClick={handleJoinGroup} disabled={joiningGroup}>
              {joiningGroup ? 'Joining…' : `Join ${joinableGroup.name}`}
            </button>
          </div>
        )}

        {!tt && !joinableGroup && notFound && (
          <div className="card" style={{ textAlign:'center' }}>
            <div style={{ fontSize:32, marginBottom:8 }}>⚠️</div>
            <h3 style={{ marginBottom:8 }}>Can't find that tee time</h3>
            <p style={{ fontSize:13, color:'#7a7a6a', marginBottom:'1.25rem' }}>
              It may have been cancelled, already happened, or you don't have access to it.
            </p>
            <button className="btn btn-accent btn-sm" onClick={() => navigate('/browse')}>Go to Greentime</button>
          </div>
        )}
      </div>
    </div>
  );
}
